'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface FaqItem {
  question: string;
  answer: string;
  category: string;
}

const faqs: FaqItem[] = [
  // Getting Started
  { category: 'Getting Started', question: 'What is HabitLab?', answer: 'HabitLab is a smart habit tracking platform that helps you build better routines, stay accountable, and achieve your goals through streaks, analytics, and gamified achievements.' },
  { category: 'Getting Started', question: 'Do I need to create an account to use HabitLab?', answer: 'Yes, you need to create a free account to use HabitLab. This ensures your data is private and secure, and allows you to access your habits from any device.' },
  { category: 'Getting Started', question: 'Is there a free plan?', answer: 'Yes! Our Free plan lets you track up to 2 health habits and 2 fitness habits, with access to the weekly tracker and dashboard. No credit card required.' },
  { category: 'Getting Started', question: 'How do I set up my first habit?', answer: 'After creating your account, navigate to your dashboard and click "Add Habit". Choose a category, set your goal type (boolean or numeric), and start tracking right away.' },
  // Plans & Billing
  { category: 'Plans & Billing', question: "What's included in each plan?", answer: 'Free: Dashboard, 2 Health + 2 Fitness habits, Weekly Tracker. Basic ($9.99/mo): Unlimited habits, analytics, streak tracking. Advanced ($19.99/mo): Everything in Basic plus weight tracking, XP system, achievements, and workout tracking.' },
  { category: 'Plans & Billing', question: 'Can I upgrade or downgrade my plan?', answer: 'Yes, you can change your plan at any time from your account settings. Upgrades take effect immediately, and downgrades apply at the end of your current billing cycle.' },
  { category: 'Plans & Billing', question: 'How do I cancel my subscription?', answer: 'You can cancel your subscription at any time from your account settings. Your access will continue until the end of your current billing period.' },
  { category: 'Plans & Billing', question: 'Do you offer refunds?', answer: 'We do not offer refunds except where required by law (e.g., Australian Consumer Law). Please contact us if you have a specific concern.' },
  { category: 'Plans & Billing', question: 'What payment methods do you accept?', answer: 'We accept all major credit and debit cards via Stripe. Your payment details are handled securely by Stripe and never stored on our servers.' },
  { category: 'Plans & Billing', question: 'Do you offer discounts or coupon codes?', answer: 'We occasionally offer promotional discounts. You can enter a coupon code on the Pricing page before subscribing.' },
  // Habits & Tracking
  { category: 'Habits & Tracking', question: 'What types of habits can I track?', answer: 'You can track habits across four categories: Health, Fitness, Personal, and Professional. Each habit can be boolean (done/not done) or numeric (e.g., drink 8 glasses of water).' },
  { category: 'Habits & Tracking', question: 'Can I track habits on specific days of the week?', answer: 'Yes, you can configure each habit to be tracked on specific days of the week, so your streaks only count on the days you intend to complete them.' },
  { category: 'Habits & Tracking', question: 'What is a streak?', answer: 'A streak is the number of consecutive days you have completed a habit. Maintaining streaks is a key motivational feature of HabitLab.' },
  { category: 'Habits & Tracking', question: 'Can I edit or delete a habit?', answer: 'Yes, you can edit or delete any habit from your dashboard at any time. Deleting a habit will remove all associated tracking data.' },
  { category: 'Habits & Tracking', question: 'What happens if I forget to log for a day?', answer: 'If you miss a day, your streak will reset to zero. We recommend logging your habits daily to maintain your progress.' },
  // Workouts & Weight
  { category: 'Workouts & Weight', question: 'How does workout tracking work?', answer: 'With the Advanced plan, you can log workouts by adding exercises, sets, reps, and weights. Your workout history is saved and viewable over time.' },
  { category: 'Workouts & Weight', question: 'Can I track both cardio and weights exercises?', answer: 'Yes, HabitLab supports both cardio (duration/distance) and strength training (sets/reps/weight) exercise types.' },
  { category: 'Workouts & Weight', question: 'How does weight tracking work?', answer: 'With the Advanced plan, you can log your body weight daily and view a trend chart over time to monitor your progress.' },
  // Achievements & Gamification
  { category: 'Achievements & Gamification', question: 'What are XP and levels?', answer: 'XP (Experience Points) are earned by completing habits, maintaining streaks, and reaching milestones. As you accumulate XP, you level up, unlocking new badges and recognition.' },
  { category: 'Achievements & Gamification', question: 'How do I earn badges?', answer: 'Badges are earned by reaching specific milestones, such as completing a 7-day streak, logging 50 habits, or achieving a personal best. There are 18+ badges to unlock.' },
  // Privacy & Security
  { category: 'Privacy & Security', question: 'Is my data private?', answer: 'Yes. Your habit data is private and only accessible to you. We do not sell or share your personal data with third parties.' },
  { category: 'Privacy & Security', question: 'Is HabitLab secure?', answer: 'Yes. We use HTTPS encryption, secure storage environments, and strict access controls to protect your data.' },
  { category: 'Privacy & Security', question: 'Can I delete my account and data?', answer: 'Yes. You can request account deletion at any time by contacting our support team. All your personal data will be permanently removed.' },
  // Technical & Support
  { category: 'Technical & Support', question: 'What devices does HabitLab work on?', answer: 'HabitLab is a web-based application that works on any modern browser on desktop, tablet, or mobile devices.' },
  { category: 'Technical & Support', question: "I'm having trouble logging in. What should I do?", answer: 'Try resetting your password using the "Forgot Password" link on the login page. If the issue persists, contact our support team.' },
  { category: 'Technical & Support', question: 'How do I contact support?', answer: 'You can reach our support team via the Contact page. We aim to respond within 24 hours on business days.' },
];

const categories = ['All Topics', 'Getting Started', 'Plans & Billing', 'Habits & Tracking', 'Workouts & Weight', 'Achievements & Gamification', 'Privacy & Security', 'Technical & Support'];

export default function FaqsPage() {
  const [activeCategory, setActiveCategory] = useState('All Topics');
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  const filteredFaqs = activeCategory === 'All Topics'
    ? faqs
    : faqs.filter(f => f.category === activeCategory);

  const groupedFaqs = categories.slice(1).reduce((acc, cat) => {
    const items = filteredFaqs.filter(f => f.category === cat);
    if (items.length > 0) acc[cat] = items;
    return acc;
  }, {} as Record<string, FaqItem[]>);

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 bg-gray-50 text-center">
        <div className="max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-gray-200 text-gray-600 text-sm font-medium rounded-full mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <path d="M12 17h.01" />
            </svg>
            Frequently Asked Questions
          </span>
          <h1 className="text-4xl font-extrabold text-gray-900 mb-4">How can we help?</h1>
          <p className="text-gray-500">
            Everything you need to know about HabitLab. Can&apos;t find what you&apos;re looking for?{' '}
            <Link href="/Contact" className="text-violet-600 hover:text-violet-700 underline">Contact us</Link>.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <div className="px-6 py-6 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto flex flex-wrap gap-2">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeCategory === cat
                  ? 'bg-violet-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* FAQ List */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto space-y-10">
          {Object.entries(groupedFaqs).map(([category, items]) => (
            <div key={category}>
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-1 h-5 bg-violet-600 rounded-full inline-block"></span>
                {category}
              </h2>
              <div className="space-y-2">
                {items.map((faq) => (
                  <div key={faq.question} className="border border-gray-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === faq.question ? null : faq.question)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-sm font-medium text-gray-800">{faq.question}</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={`flex-shrink-0 text-gray-400 transition-transform ${openFaq === faq.question ? 'rotate-180' : ''}`}
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </button>
                    {openFaq === faq.question && (
                      <div className="px-5 pb-4">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Still have questions CTA */}
      <section className="py-16 px-6 text-center">
        <div className="max-w-md mx-auto">
          <div className="w-12 h-12 bg-violet-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-violet-600">
              <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Still have questions?</h2>
          <p className="text-gray-500 text-sm mb-6">Can&apos;t find the answer you&apos;re looking for? Our team is happy to help.</p>
          <Link href="/Contact" className="inline-block px-8 py-3.5 bg-violet-600 hover:bg-violet-500 text-white font-semibold rounded-xl transition-all">
            Get in Touch
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
